import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import StagingGate from './components/StagingGate';

// Lazy load route components for code splitting
const WaitingListPage = lazy(() => import('./pages/WaitingListPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const EpicCallbackPage = lazy(() => import('./pages/EpicCallbackPage'));
const TemporaryProfilePage = lazy(() => import('./pages/TemporaryProfilePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const FlexPointsDashboard = lazy(() => import('./components/FlexPoints/FlexPointsDashboard'));
const RewardsPage = lazy(() => import('./pages/RewardsPage'));
const LinkAccounts = lazy(() => import('./components/LinkAccounts'));
const ChallengesPage = lazy(() => import('./pages/ChallengesPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const StrapiPageTest = lazy(() => import('./components/StrapiPageTest'));

// Check if we're in staging environment
const isStagingEnvironment = (): boolean => {
  // Check environment variable first
  if (process.env.REACT_APP_ENVIRONMENT === 'staging') {
    return true;
  }
  
  // Check hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  return hostname.includes('staging') || hostname === 'staging.fandomid.com' || hostname.includes('staging.app.fandomid.com');
};

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#18181a',
    color: '#fff'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid rgba(255, 255, 255, 0.1)',
        borderTopColor: '#fff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }} />
      <p>Loading...</p>
    </div>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  const isStaging = isStagingEnvironment();
  
  const appContent = (
    <>
      <Router>
        <div className="App">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<WaitingListPage />} />
              <Route path="/waitlist" element={<WaitingListPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <TemporaryProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/flex-points" element={
                <ProtectedRoute>
                  <FlexPointsDashboard />
                </ProtectedRoute>
              } />
              <Route path="/redeem" element={
                <ProtectedRoute>
                  <RewardsPage />
                </ProtectedRoute>
              } />
              <Route path="/connect-platforms" element={
                <ProtectedRoute>
                  <LinkAccounts />
                </ProtectedRoute>
              } />
              <Route path="/challenges" element={
                <ProtectedRoute>
                  <ChallengesPage />
                </ProtectedRoute>
              } />
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              } />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPage />} />
              <Route path="/strapi-test" element={<StrapiPageTest />} />
              <Route path="/auth/epic/callback" element={<EpicCallbackPage />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </>
  );

  // Wrap with StagingGate if in staging environment
  if (isStaging) {
    return <StagingGate>{appContent}</StagingGate>;
  }

  return appContent;
}

export default App;
